import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Form,
  InputGroup,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { authFetch } from "../utils/authFetch";
import { toast } from "react-toastify";

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export default function RecipeList() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const skip = currentPage * itemsPerPage;
      const usp = new URLSearchParams();
      if (searchTerm.trim()) {
        usp.set("q", searchTerm.trim());
      }
      usp.set("skip", String(skip));
      usp.set("limit", String(itemsPerPage));

      const qs = usp.toString();
      const data = await authFetch(`/bxreteta/?${qs}`);

      // Filter active client-side or backend-side
      let filtered = Array.isArray(data) ? data : data?.items || [];
      if (showActiveOnly) {
        filtered = filtered.filter((item) => item.activa === 1);
      }

      setItems(filtered);
      setTotalCount(
        Array.isArray(data) ? data.length : data?.total || filtered.length,
      );
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Sigur doriți să ștergeți această rețetă cu toate materialele ei?",
      )
    ) {
      return;
    }
    try {
      await authFetch(`/bxreteta/${id}`, { method: "DELETE" });
      toast.success("Rețeta a fost ștearsă.");
      fetchList();
    } catch (e) {
      toast.error(`Eroare la ștergere: ${e.message || e}`);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
  }, [currentPage, itemsPerPage, searchTerm, showActiveOnly, sortBy, sortDir]);

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const from = totalCount ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalCount, (currentPage + 1) * itemsPerPage);

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h3 className="mb-0">Retete</h3>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/retete/new" variant="success">
            + Rețetă nouă
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Cauta după produs finit, cod sau denumire reteta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col md={3} className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            label="Doar rețete active"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
          />
        </Col>

        <Col
          md={3}
          className="d-flex justify-content-end align-items-center gap-2"
        >
          <Form.Select
            size="sm"
            style={{ width: 110 }}
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/pag.
              </option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={fetchList}>
            Caută
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover size="sm" className="align-middle">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: "80px" }}>
                  Activă
                </th>
                <th>Cod</th>
                <th>Denumire Rețetă</th>
                <th>Produs Finit</th>
                <th>Valabilitate Start</th>
                <th>Valabilitate Stop</th>
                <th style={{ width: 180 }} />
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-3">
                    Nicio rețetă găsită.
                  </td>
                </tr>
              ) : null}

              {items.map((it) => (
                <tr key={it.id}>
                  <td className="text-center fs-5">
                    {it.activa === 1 ? "⭐" : ""}
                  </td>
                  <td>
                    <strong>{it.cod_reteta}</strong>
                  </td>
                  <td>{it.denumire}</td>
                  <td>
                    <strong>{it.produs_finit_cod_str || "-"}</strong> —{" "}
                    {it.produs_finit_denumire || "-"}
                  </td>
                  <td>{formatDateRo(it.data_start) || "-"}</td>
                  <td>{formatDateRo(it.data_stop) || "-"}</td>
                  <td className="text-end">
                    <Button
                      as={Link}
                      size="sm"
                      variant="primary"
                      to={`/retete/${it.id}`}
                      className="me-2"
                    >
                      Editează
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(it.id)}
                    >
                      Șterge
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            renderOnZeroPageCount={null}
          />
        </>
      )}
    </Container>
  );
}
